'use client'

import React, { useEffect, useState, useRef, useMemo } from 'react'
import cn from 'classnames'
import { ChevronDownIconWhite } from '@/icons/ChevronDownIconWhite'

export interface DropDownSearchOption {
  code: string
  label: string
  testId?: string
}

interface DropDownSearchProps<T extends DropDownSearchOption> {
  id: string
  label?: string
  options: T[]
  selectedValue?: string
  placeholder?: string
  isLoading?: boolean
  loadingText?: string
  noResultsText?: string
  hasError?: boolean
  errorMessage?: string
  onChange?: (option: T) => void
  onBlur?: () => void
  disabled?: boolean
  className?: string
  testId?: string
}

export function DropDownSearch<T extends DropDownSearchOption>({
  id,
  label,
  options,
  selectedValue,
  placeholder = '',
  isLoading = false,
  loadingText = 'Loading...',
  noResultsText = 'No results',
  hasError = false,
  errorMessage,
  onChange,
  onBlur,
  disabled = false,
  className,
  testId,
}: DropDownSearchProps<T>) {
  const [isOpen, setIsOpen] = useState(false)
  const [searchQuery, setSearchQuery] = useState('')
  const dropdownRef = useRef<HTMLDivElement>(null)
  const inputRef = useRef<HTMLInputElement>(null)

  // Find selected option from options list
  const selectedOption = useMemo(() => {
    return options.find((opt) => opt.code === selectedValue)
  }, [options, selectedValue])

  // Filter options by search query
  const filteredOptions = useMemo(() => {
    if (!searchQuery) return options
    return options.filter((option) => option.label.toLowerCase().includes(searchQuery.toLowerCase()))
  }, [options, searchQuery])

  // Handle click outside to close dropdown
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false)
        setSearchQuery('')
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  const handleSelect = (option: T) => {
    onChange?.(option)
    setSearchQuery('')
    setIsOpen(false)
    onBlur?.()
  }

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(e.target.value)
    if (!isOpen) setIsOpen(true)
  }

  const handleInputFocus = () => {
    if (!disabled && !isLoading) {
      setIsOpen(true)
    }
  }

  const handleArrowClick = () => {
    if (!disabled && !isLoading) {
      setIsOpen(!isOpen)
      if (!isOpen) {
        inputRef.current?.focus()
      }
    }
  }

  const displayValue = isOpen ? searchQuery : (selectedOption?.label ?? '')

  return (
    <div data-testid={testId} className={cn('flex w-full flex-col gap-1.5', className)} ref={dropdownRef}>
      {label && (
        <label htmlFor={id} className="text-neutral-darker text-sm leading-none">
          {label}
        </label>
      )}
      <div className="relative">
        <input
          ref={inputRef}
          type="text"
          id={id}
          value={isLoading ? loadingText : displayValue}
          onChange={handleInputChange}
          onFocus={handleInputFocus}
          disabled={disabled || isLoading}
          placeholder={placeholder || selectedOption?.label}
          className={cn(
            'w-full rounded-lg border bg-white px-2.5 py-1.5 pe-10 text-base leading-none',
            'focus:border-primary-base focus:outline-none',
            hasError ? 'border-error' : 'border-neutral-medium-light',
            (disabled || isLoading) && 'cursor-not-allowed opacity-50'
          )}
        />
        {/* Arrow icon */}
        <button
          type="button"
          onClick={handleArrowClick}
          disabled={disabled || isLoading}
          className="absolute inset-y-0 end-2.5 flex items-center"
        >
          <ChevronDownIconWhite
            color="neutral-medium-light"
            className={cn('h-4 w-4 transition-transform', isOpen && 'rotate-180')}
          />
        </button>

        {/* Dropdown menu */}
        {isOpen && !isLoading && (
          <ul className="border-neutral-medium-light absolute z-10 mt-1 max-h-48 w-full overflow-auto rounded-lg border bg-white shadow-lg">
            {filteredOptions.length > 0 ? (
              filteredOptions.map((option) => (
                <li
                  key={option.code}
                  data-testid={option.testId}
                  onClick={() => handleSelect(option)}
                  className={cn(
                    'cursor-pointer px-2.5 py-2 text-base leading-none',
                    'hover:bg-neutral-very-light',
                    selectedOption?.code === option.code && 'bg-neutral-very-light font-medium'
                  )}
                >
                  {option.label}
                </li>
              ))
            ) : (
              <li className="text-neutral-medium-light px-2.5 py-2 text-base leading-none">{noResultsText}</li>
            )}
          </ul>
        )}
      </div>
      {hasError && errorMessage && <span className="text-error text-xs">{errorMessage}</span>}
    </div>
  )
}

export default DropDownSearch
